import { Component, inject, OnInit } from '@angular/core'
import { LoggerService } from '@danangelus/ngx-app-core'
import { ButtonModule } from 'primeng/button'
import { InputGroupModule } from 'primeng/inputgroup'
import { InputGroupAddonModule } from 'primeng/inputgroupaddon'
import { PaginatorModule, PaginatorState } from 'primeng/paginator'
import { SessionService } from '../../../../core/security/session.service'
import { User } from '../../../../models/user.model'
import { TableModule } from 'primeng/table'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { InputTextModule } from 'primeng/inputtext'
import { LdapManagerService } from '../../../../core/services/ldap-manager.service'
import { Router } from '@angular/router'
import { debounceTime, distinctUntilChanged, Subject, switchMap, tap } from 'rxjs';

const log = LoggerService.get('ListUserComponent')

/**
 * Component responsible for the searching and display of LDAP Users via the {@link LdapManagerService}
 *
 * @author Hayden Eastwell
 */
@Component({
  selector: 'list-user',
  standalone: true,
    imports: [
        // PrimeNG
        ButtonModule,
        IconFieldModule,
        InputGroupAddonModule,
        InputGroupModule,
        InputIconModule,
        InputTextModule,
        PaginatorModule,
        TableModule,
    ],
  templateUrl: './list-user.component.html',
  styleUrl: './list-user.component.sass'
})
export class ListUserComponent implements OnInit {

    // services
    ldapManagerService = inject(LdapManagerService)
    router = inject(Router)
    sessionService = inject(SessionService)

    // data needed for sorting and pagination
    loading = true
    sortColumn = 'name'
    sortedAsc = true
    pageNumber = 1
    pageSize = 25
    totalRecords = 0
    firstRecord = 1

    // data needed for searching groups in search bar
    query = ''
    searchSubject = new Subject<string>()

    users: User[] = []

    async ngOnInit(): Promise<void> {
        this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(query => {
                this.query = query.length > 2 ? query : ''
                if (query.length <= 2) {
                    this.pageNumber = 1 // Reset to the first page when clearing the query
                }
            }),
            switchMap(async () => this.users = await this.getUsers())
        ).subscribe()
        this.searchSubject.next(this.query)
    }

    /**
     * Method for calling on {@linkcode LdapManagerService.getUsers} with an optional query
     * @param sortColumn optional column to sort by, defaults to name in ascending order
     */
    async getUsers(sortColumn = `${this.sortColumn}-${this.sortedAsc ? 'asc' : 'desc'}`): Promise<User[]> {
        log.debug('getUsers', `Finding users ${this.query}`)
        this.loading = true
        const page = await this.ldapManagerService.findUsers(this.query, this.isDealerAdmin, this.pageNumber, this.pageSize, sortColumn)
        this.totalRecords = page?.totalElements || 0
        this.loading = false
        return page?.content.map((user: any) => new User(user)) || []
    }

    async changePage(event: PaginatorState) {
        log.debug('changePage', `Finding users ${this.query}`)
        this.pageNumber = event.page! + 1
        this.pageSize = event.rows!
        this.users = await this.getUsers()
    }

    async sortBy(column: any): Promise<void> {
        if (this.sortColumn === column.field) {
            this.sortedAsc = !this.sortedAsc
        }
        const sortColumn = `${column.field}-${this.sortedAsc ? 'asc' : 'desc'}`
        this.sortColumn = column.field
        // FixMe :: Handle INTERNAL_SERVER error
        const users = await this.getUsers(sortColumn)
        this.users.splice(0, this.users.length)
        this.users.push(...users)
        log.debug('sortBy', `Sorted by ${sortColumn}`)
    }

    /**
     * Method for handling the input event from a {@linkcode HTMLInputElement}
     * @param event {@linkcode Event} to extract a value from
     */
    async search(event: Event) {
        log.debug('search', 'Searching for users')
        const queryStr = (event.target as HTMLInputElement).value

        if (queryStr === '' && this.query !== '') {
            // Explicitly trigger search for cleared input
            this.searchSubject.next('');
        } else {
            this.searchSubject.next(queryStr);
        }
    }

    get isDealerAdmin() {
        return this.sessionService.user()!.groups?.indexOf('DEALERADMIN') > -1
    }

    async createUser(): Promise<void> {
        log.debug('createUser', 'Creating a new user')
        await this.router.navigateByUrl(`/administration/${this.isDealerAdmin ? 'sub-user' : 'user' }/create`)
    }

    async editUser(id: string): Promise<void> {
        log.debug('editUser', 'Editing a user')
        await this.router.navigateByUrl(`/administration/${this.isDealerAdmin ? 'sub-user' : 'user'}/${id}`)
    }

}
